import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";

/** CSS affecting the {@link CanvasComponent} */

const styles: string = css`
    :host {
        font-family: "Onesize";
        width: 100%;
        max-width: 1024px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        /* grid-template-rows: auto calc(40vh + 10px) minmax(calc(35vh + 10px), 1fr) auto; */
        grid-template-rows: auto calc(60vh + 10px) auto auto;
        grid-column-gap: 0px;
        grid-row-gap: 0px;
    }

    ::-webkit-scrollbar {
        width: 0.3em;
      }
      
      ::-webkit-scrollbar-thumb {
        background: #ffffff;
      }

    .title {
        text-align: center;
        margin-top: 10px;
        overflow: auto;
        text-shadow: 2px 2px 4px #000000;
    }
    
    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        margin-top: 10px;
    }
    
    .header img {
        width: auto;
        max-width: 100%;
        height: auto;
        max-height: 100%;
        image-rendering: pixelated;
    }
    
    .header img:nth-child(n + 2) {
        position: absolute;
    }
    
    .content {
        flex-grow: 1;
        position: relative;
        //overflow: auto;
        margin-top: 10px;
        padding: 0 10px;
        z-index: 5;
    }

    .content .absolute {
        position: absolute;
        bottom: 0;
    }
    
    .content p {
        margin: 0 0 10px 0;
        text-shadow: 2px 2px 4px #000000;
    }
    
    .content p:last-of-type {
        margin: 0;
    }
    
    .footer {
        align-items: center;
        margin-top: 10px;
        height: 200px;
        overflow: hidden;
        display: grid;
        grid-template-columns: 1fr auto;
        grid-auto-flow: column;
    }
    
    .footer .buttons {
        display: flex;
        flex-direction: column;
        overflow: auto;
        padding: 10px 10px 0 10px;
        overflow: auto;
        height: 100%;
    }
    
    .footer .button {
        background-color: #ffffff;
        /* border: 1px solid #332c57; */
        color:rgb(0, 0, 0);
        padding: 10px 20px;
        border-radius: 8px;
        margin: 0 0 10px 10px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;

        clip-path: polygon(
            0px calc(100% - 15px),
            3px calc(100% - 15px),
            3px calc(100% - 9px),
            6px calc(100% - 9px),
            6px calc(100% - 6px),
            9px calc(100% - 6px),
            9px calc(100% - 3px),
            15px calc(100% - 3px),
            15px 100%,
            calc(100% - 15px) 100%,
            calc(100% - 15px) calc(100% - 3px),
            calc(100% - 9px) calc(100% - 3px),
            calc(100% - 9px) calc(100% - 6px),
            calc(100% - 6px) calc(100% - 6px),
            calc(100% - 6px) calc(100% - 9px),
            calc(100% - 3px) calc(100% - 9px),
            calc(100% - 3px) calc(100% - 15px),
            100% calc(100% - 15px),
            100% 15px,
            calc(100% - 3px) 15px,
            calc(100% - 3px) 9px,
            calc(100% - 6px) 9px,
            calc(100% - 6px) 6px,
            calc(100% - 9px) 6px,
            calc(100% - 9px) 3px,
            calc(100% - 15px) 3px,
            calc(100% - 15px) 0px,
            15px 0px,
            15px 3px,
            9px 3px,
            9px 6px,
            6px 6px,
            6px 9px,
            3px 9px,
            3px 15px,
            0px 15px
          );
    }

    .footer .buttons .actions a{
        background-color:rgb(136, 123, 204);

    }

    .footer inventory-component {
        height: 100%;
        overflow: hidden;
        
    }
    
    .footer .button.active,
    .footer .button:hover {
        background-color: #332c57;
        color: #ffffff;
    }
`;

/**
 * Represents the Canvas page
 */
export class CanvasComponent extends HTMLElement {
    /** Event name for closing the on-top component */
    public static readonly CLOSE_ON_TOP_COMPONENT_EVENT = "close-on-top-component";

    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();

    /**
     * Static method to close the on-top component from a subcomponent
     * This method dispatches a custom event that will be caught by the CanvasComponent
     */
    public static closeFromSubcomponent(): void {
        document.dispatchEvent(new CustomEvent(CanvasComponent.CLOSE_ON_TOP_COMPONENT_EVENT));
    }

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        // Add event listener for closing the on-top component
        document.addEventListener(CanvasComponent.CLOSE_ON_TOP_COMPONENT_EVENT, this.handleCloseOnTopComponent);

        void this.refreshGameState();
    }

    /**
     * Clean up event listeners when component is removed
     */
    public disconnectedCallback(): void {
        // Remove event listener for closing the on-top component
        document.removeEventListener(CanvasComponent.CLOSE_ON_TOP_COMPONENT_EVENT, this.handleCloseOnTopComponent);
    }

    /**
     * Handle the close on-top component event
     */
    private handleCloseOnTopComponent = (): void => {
        this.closeOnTopComponent();
    };

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
    }

    /**
     * Close the on-top component by updating the game state without it
     * This method can be called from a subcomponent to close itself
     */
    public closeOnTopComponent(): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!this._currentGameState || this._currentGameState.type !== "default") {
            return;
        }

        // Create a new game state without the onTopComponent
        const newState: DefaultGameState = {
            ...this._currentGameState,
            onTopComponent: undefined,
        };

        // Update the game state
        this.updateGameState(newState);
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}

                .title {
                    grid-area: 1 / 1 / 2 / 2;
                }

                .header {
                    grid-area: 2 / 1 / 3 / 2;
                }

                .content {
                    grid-area: 3 / 1 / 4 / 2;
                }

                .footer {
                    grid-area: 4 / 1 / 5 / 2;
                }

                ${this.getOntopElementString()} {
                    grid-area: 2 / 1 / 4 / 2;
                    /*visibility: hidden;*/
                }

                ${this.getOntopElementString()}:before {
                    position: absolute;
                    background: rgba(0,0,0,0.5);
                    width: 100%;
                    height: 100%;
                    content: "";
                    z-index: -1;
                    pointer-events: none;
                }

                ${this.getOntopElementString()} > * {
                    z-index: 2;
                }

                :host:before{
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: url('/assets/img/rooms/${this._currentGameState?.roomImages[0]}.png');
                    background-size: cover;
                    background-position: center;
                    z-index: -1;
                    filter: blur(7px);
                }
            </style>

            ${this.renderTitle()}
            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}

            ${this.renderOnTop()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the title element
     *
     * @returns String with raw HTML for the title element. Can be empty.
     */
    private renderTitle(): string {
        const roomName: string | undefined = this._currentGameState?.roomName;

        if (roomName) {
            return `<div class="title">${roomName}</div>`;
        }

        return "";
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        const roomImages: string[] | undefined = this._currentGameState?.roomImages;

        if (roomImages && roomImages.length > 0) {
            return `
                <div class="header">
                    ${roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("")}
                </div>
            `;
        }

        return "";
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
            <div class="content">
                <div class="absolute">
                    ${this._currentGameState?.text.map(text => `<p>${text}</p>`).join("") || ""}
                </div>
            </div>
        `;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`
            <div class="footer">
                <div class="buttons">
                    <div class="actions">
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div class="objects">
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                    
                </div>
                <inventory-component></inventory-component>        
            </div>
        `;
    }

    private renderOnTop(): HTMLElement {
        if (!this._currentGameState?.onTopComponent) return html`<div></div>`;
        return html`
            <${this.getOntopElementString(true)}></${this.getOntopElementString(true)}>
        `;
    }

    private getOntopElementString(withType: boolean = false): string | undefined {
        if (this._currentGameState?.onTopComponent) {
            const splittedComponent: string[] = this._currentGameState.onTopComponent.split("-");
            if (!withType) return "game-" + splittedComponent[0];
            if (splittedComponent.length > 1) {
                let type: string = " type='";
                for (let i: number = 1; i < splittedComponent.length; i++) {
                    type += splittedComponent[i];
                }
                type += "'";
                return "game-" + splittedComponent[0] + type;
            }
            return "game-" + splittedComponent[0];
        }
        return undefined;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedActionButton === button ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const element: HTMLElement = html`
            <a class="button ${this._selectedGameObjectButtons.has(button) ? "active" : ""}">
                ${button.name}
            </a>
        `;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
}
